// Offline mutation queue for the operator-studio + collector PWA flows.
//
// Pattern: try the network first. On a network error (or when navigator.onLine
// is false), persist the mutation locally with a client-generated UUID v7 as
// the idempotency key. When connectivity returns, drain the queue in order.
// The backend dedupes via the idempotency_key so replays are safe.
//
// Adapter is abstracted so unit tests can use an in-memory store; the real
// PWA uses IndexedDB.

export type QueuedEndpoint = 'ledger.mark_paid' | 'cashbook.create_entry';

export interface QueuedMutation {
  /** Stable client-generated id. Also the payment.idempotency_key on the wire. */
  id: string;
  endpoint: QueuedEndpoint;
  payload: unknown;
  created_at: number;
  retries: number;
  last_error: string | null;
}

export interface QueueAdapter {
  list(): Promise<QueuedMutation[]>;
  put(m: QueuedMutation): Promise<void>;
  remove(id: string): Promise<void>;
  clear(): Promise<void>;
}

export interface OfflineQueueOptions {
  /** Max retries before a mutation is removed. Default 5. */
  max_retries?: number;
  /** Callback fired whenever the queue contents change. */
  on_change?: () => void;
}

/** Crude type for the orpc-client surface we actually use, so tests can mock it. */
export interface MutationDispatcher {
  ledger_mark_paid: (input: unknown) => Promise<unknown>;
  cashbook_create_entry: (input: unknown) => Promise<unknown>;
}

export interface DrainResult {
  attempted: number;
  succeeded: number;
  failed: number;
  removed_for_max_retries: number;
}

export class OfflineQueue {
  constructor(
    private readonly adapter: QueueAdapter,
    private readonly options: OfflineQueueOptions = {},
  ) {}

  async list(): Promise<QueuedMutation[]> {
    const items = await this.adapter.list();
    items.sort((a, b) => a.created_at - b.created_at);
    return items;
  }

  async enqueue(endpoint: QueuedEndpoint, payload: unknown): Promise<QueuedMutation> {
    const item: QueuedMutation = {
      id: generate_client_id(),
      endpoint,
      payload,
      created_at: Date.now(),
      retries: 0,
      last_error: null,
    };
    await this.adapter.put(item);
    this.options.on_change?.();
    return item;
  }

  async remove(id: string): Promise<void> {
    await this.adapter.remove(id);
    this.options.on_change?.();
  }

  async clear(): Promise<void> {
    await this.adapter.clear();
    this.options.on_change?.();
  }

  /**
   * Replay queued mutations against the network. Returns counts of
   * succeeded / failed / max-retries-removed items.
   *
   * Each item carries its idempotency_key (== item.id) so a server that has
   * already accepted the mutation will return the existing record without
   * mutating state.
   */
  async drain(dispatcher: MutationDispatcher): Promise<DrainResult> {
    const max_retries = this.options.max_retries ?? 5;
    const items = await this.list();
    let succeeded = 0;
    let failed = 0;
    let removed_for_max_retries = 0;

    for (const item of items) {
      try {
        await dispatch_one(dispatcher, item);
        await this.adapter.remove(item.id);
        succeeded += 1;
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        const next_retries = item.retries + 1;
        if (next_retries >= max_retries) {
          await this.adapter.remove(item.id);
          removed_for_max_retries += 1;
        } else {
          await this.adapter.put({
            ...item,
            retries: next_retries,
            last_error: message,
          });
        }
        failed += 1;
      }
    }

    this.options.on_change?.();
    return {
      attempted: items.length,
      succeeded,
      failed,
      removed_for_max_retries,
    };
  }
}

async function dispatch_one(d: MutationDispatcher, item: QueuedMutation): Promise<void> {
  // Each item's `id` becomes the payment.idempotency_key (or cashbook entry idempotency)
  // so replays are no-ops on the server.
  if (item.endpoint === 'ledger.mark_paid') {
    const payload = item.payload as { subscription_id: string; payment: Record<string, unknown> };
    await d.ledger_mark_paid({
      subscription_id: payload.subscription_id,
      payment: { ...payload.payment, idempotency_key: item.id },
    });
    return;
  }
  if (item.endpoint === 'cashbook.create_entry') {
    await d.cashbook_create_entry(item.payload);
    return;
  }
  throw new Error(`unknown queued endpoint: ${item.endpoint as string}`);
}

// ─── Adapters ────────────────────────────────────────────────────────────────

/** In-memory adapter — used by tests, and as a degenerate fallback when IDB
 *  isn't available (e.g., SSR pre-hydration). */
export class InMemoryQueueAdapter implements QueueAdapter {
  private readonly store = new Map<string, QueuedMutation>();
  async list(): Promise<QueuedMutation[]> {
    return Array.from(this.store.values()).map((m) => ({ ...m }));
  }
  async put(m: QueuedMutation): Promise<void> {
    this.store.set(m.id, { ...m });
  }
  async remove(id: string): Promise<void> {
    this.store.delete(id);
  }
  async clear(): Promise<void> {
    this.store.clear();
  }
}

/** IndexedDB-backed adapter. Only constructible in the browser. */
export class IdbQueueAdapter implements QueueAdapter {
  private readonly db_name: string;
  private readonly store_name = 'mutations';
  private db_promise: Promise<IDBDatabase> | null = null;

  constructor(db_name = 'chitos-offline-queue') {
    this.db_name = db_name;
  }

  private open(): Promise<IDBDatabase> {
    if (this.db_promise) return this.db_promise;
    this.db_promise = new Promise((resolve, reject) => {
      const req = indexedDB.open(this.db_name, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(this.store_name)) {
          db.createObjectStore(this.store_name, { keyPath: 'id' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    return this.db_promise;
  }

  private async tx<T>(
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => IDBRequest<T> | null,
  ): Promise<T> {
    const db = await this.open();
    return new Promise<T>((resolve, reject) => {
      const tx = db.transaction(this.store_name, mode);
      const store = tx.objectStore(this.store_name);
      const req = run(store);
      tx.oncomplete = () => {
        if (req === null) resolve(undefined as unknown as T);
        else resolve(req.result);
      };
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error ?? new Error('IDB tx aborted'));
    });
  }

  async list(): Promise<QueuedMutation[]> {
    return this.tx('readonly', (store) => store.getAll() as IDBRequest<QueuedMutation[]>);
  }
  async put(m: QueuedMutation): Promise<void> {
    await this.tx('readwrite', (store) => store.put(m));
  }
  async remove(id: string): Promise<void> {
    await this.tx('readwrite', (store) => store.delete(id));
  }
  async clear(): Promise<void> {
    await this.tx('readwrite', (store) => store.clear());
  }
}

// ─── Client-side id generation ──────────────────────────────────────────────
// Mirror @chitos/utils generate_id() — UUID v7 (time-ordered) for consistency
// with the rest of the system. Avoids pulling that dependency into a path that
// must be browser-safe.

function generate_client_id(): string {
  // Use Web Crypto getRandomValues for the random bits; fall back to Math.random
  // for environments that lack it (vitest-node sometimes does).
  const rand = new Uint8Array(10);
  const g = globalThis as { crypto?: { getRandomValues?: (a: Uint8Array) => void } };
  if (g.crypto?.getRandomValues) {
    g.crypto.getRandomValues(rand);
  } else {
    for (let i = 0; i < rand.length; i++) rand[i] = Math.floor(Math.random() * 256);
  }
  const now_ms = Date.now();
  const ts_hex = now_ms.toString(16).padStart(12, '0');
  const hex = (n: number): string => n.toString(16).padStart(2, '0');
  // version 7 = high nibble of byte 6 = 0x7, variant high bits = 10 in byte 8
  rand[0] = (0x70 | (rand[0]! & 0x0f)) & 0xff;
  rand[2] = (0x80 | (rand[2]! & 0x3f)) & 0xff;
  const r = Array.from(rand, hex).join('');
  return (
    `${ts_hex.slice(0, 8)}-${ts_hex.slice(8, 12)}-` +
    `${r.slice(0, 4)}-${r.slice(4, 8)}-${r.slice(8)}`
  );
}

/** Test hook — surfaces the id generator so smoke tests can assert UUID v7 shape. */
export const _internal = { generate_client_id };
